/** @module csi/widget/_svg/DropTarget */
define([
	"dojo/_base/declare",
	"dojo/query",
	"canvas/widgets/interfaces/model/enumerations/CanvasMode",
	"csi/_base/underscore",
	"csi/card/_base",
	"csi/comm",
	"csi/image/_base",
	"csi/widget/DndSource",
	"csi/reflection",
	"./util"
], function(declare, query, {CanvasMode}, _, csiCard, comm, csiImage, DndSource, reflection, util) {

	return declare(DndSource, {
		accept: [util.DND_TYPE_IMAGE],
		editor: null,
		isSource: false,

		/** @property {Object} invalidExts - map of unsupported image extensions */
		invalidExts: {
			"bmp": 1
		},

		/**
		 * Check to see that a valid image exists and that canvas is currently in edit mode
		 * @param {DndSource} source
		 * @param {DOMNode[]} nodes
		 */
		checkAcceptance: function(source, nodes) {
			const res = this.inherited(arguments);
			const {editor, invalidExts} = this;
			const {insertNode} = editor;
			if (res && editor.editMode) {
				// add inactive drop zone dashed grey border when starting drag
				insertNode.classList.add("csi-canvas-drag-over-inactive");
				return _.some(nodes, function(node) {
					const gridMenuItem = node.id.match("grid-menu-item");
					// If node is a template without a correct snapshot, allow it
					if (gridMenuItem || node.innerHTML.includes("iconSaveTemplate")) {
						return true;
					} else if (node.innerHTML.includes("csi-attribute-card")) {
						const {view} = editor;
						const refNodeUrl = view.getRefNodeUrl(node);
						const refNode = view.cacheView.get(refNodeUrl);
						editor.canvasApp.selectionApi().adjustHighlight(refNode.$Type);
						return true;
					}
					const img = (node.tagName.toLowerCase() == "img") ? node : query("img", node)[0];
					if (img && !invalidExts[img.getAttribute("srcExt")]) {
						editor.canvasApp.selectionApi().adjustHighlight("ImagePlaceholder");
						return true;
					}
				}, this);
			}

			return false;
		},

		/**
		 * This is required for the text in canvas to be selectable while editing
		 */
		onSelectStart: function() { },

		/**
		 * Convert given nodes into image objects and add to canvas
		 * @param {DndSource} source
		 * @param {DOMNode[]} nodes
		 */
		onDropExternal: async function(source, nodes) {
			const {editor} = this;

			const {
				canvasApp,
				view,
			} = editor;

			// change mode of canvas back to select
			editor.canvasApp.canvasApi().setCanvasMode(CanvasMode.SELECT);

			const {dndType} = source.grid;

			let urls;
			if (dndType === "template") {
				urls = nodes.map(node => node.nodeId);
			} else {
				urls = _.map(nodes, function(domNode) {
					return source.grid.row(domNode).id;
				});
			}

			const req = comm.getNodeRequest(urls);

			let response;
			try {
				response = await view.cacheView.addRequestDeferred(req);
			} catch (err) {
				console.err(err);
				return;
			}

			const resultNodes = response.getResultNodes();
			if (dndType == "image") {
				const images = resultNodes.map((image, index) => {
					let url = urls[index];
					const domNode = nodes[index];

					const img = (domNode.tagName.toLowerCase() == "img") ? domNode : query("img", domNode)[0];

					if (img && !this.invalidExts[img.getAttribute("srcExt")]) {
						let imgSrc = img.getAttribute("srcDrop");

						if (!imgSrc) {
							url = view.getRefNodeUrl(domNode);
							const imgCxt = view.getContext(url);
							const imgNode = imgCxt.getNode();
							imgSrc = csiImage.getImageSrc(imgNode, null, reflection.ATTR_VIEWABLE);
						}

						return {
							alt: img.getAttribute("alt"),
							src: imgSrc
						};
					}
				});

				const placeholderTarget = canvasApp.selection.getImagePlaceholderTarget();

				if (placeholderTarget) {
					editor.insertImageAtPlaceholder(images[0], placeholderTarget);
				} else {
					editor.addImages(images);
				}

			} else if (dndType == "template") {

				// add contents to page
				editor.addObjectsFromNode(resultNodes[0]);

				// deselect all objects
				canvasApp.selectionApi().deselectAllObjects();

			} else if (dndType == "placeholder") {

				const sourceNode = resultNodes[0];
				const sourceType = sourceNode.$Type;

				let placeholderTarget = canvasApp.selection.getAttributePlaceholderTarget();
				let placeholderTargetExistsAndIsFillable = false;

				if (placeholderTarget) {
					const placeholderTargetType = util.getStartTypeForKey(placeholderTarget.keys[0]);
					const sourceClass = reflection.getClass(sourceType);
					if (sourceClass.isDerivedFrom(placeholderTargetType)) {
						placeholderTargetExistsAndIsFillable = true;
					}
				}

				if (!placeholderTargetExistsAndIsFillable) {
					// C8-19102
					// Dragging to blank spot in Canvas - Create Default Placeholder
					const cardSpecs = csiCard.getAttributeCardSlimSpecs(sourceType);
					const attributeSpecs = cardSpecs.attributeSpecs;
					let imageSpec = cardSpecs.imageSpec;

					if (sourceType == "ProductPresentationSetItem") {
						imageSpec = util.getImageSpecForSetItem(sourceNode, view);
					}

					const attributeKeys = _.map(attributeSpecs, spec => spec.getKey());
					const imageFlags = _.map(attributeSpecs, spec => false);

					if (imageSpec) {
						attributeKeys.push(imageSpec.getKey());
						imageFlags.push(true);
					}

					// put in corner with images
					const position = {
						x: 0,
						y: 0,
					};

					// Put the placeholder data together into one object
					const placeholderData = {
						type: sourceType,
						attributeKeys,
						imageFlags,
						position,
					};

					// Add the Placeholder to Canvas at the location passed in from the mouse
					canvasApp.pageApi().addPlaceholders(placeholderData);

					// Get the newly selected placeholder and store it into a local variable
					placeholderTarget = canvasApp.selection.getSelectedAttributePlaceholder();
				}

				// clear selection from source
				source.anchor.classList.remove("dojoDndItemAnchor");

				// Fill the new placeholder with data that was dragged out onto Canvas
				editor.fillPlaceholderWithAttributes(sourceNode, placeholderTarget);
			}

			view.clearImageSelections();
		}
	});
});
